import { type LeadListItem } from '@utazzo/shared';
import { UzButton, UzDialog, UzSelect, UzSpinner } from '@utazzo/ui';
import { useState } from 'react';

import { useAgentOptionsForLeads } from '../use-lead-options';
import { useBulkAssignExitUnassigned } from '../use-leads';

interface ExitUnassignedAssignDialogProps {
  open: boolean;
  rows: ReadonlyArray<LeadListItem>;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const ExitUnassignedAssignDialog = ({
  open,
  rows,
  onOpenChange,
  onSuccess,
}: ExitUnassignedAssignDialogProps): React.JSX.Element => {
  const agents = useAgentOptionsForLeads();
  const assign = useBulkAssignExitUnassigned();
  const [agentId, setAgentId] = useState('');

  const handleSave = (): void => {
    if (!agentId || rows.length === 0) return;
    assign.mutate(
      { ids: rows.map((r) => r.id), assignedAgentId: agentId },
      {
        onSuccess: (res) => {
          if (res.failed.length === 0) {
            onOpenChange(false);
            setAgentId('');
            onSuccess?.();
          }
        },
      },
    );
  };

  // Bulk assign is one POST for the whole selection - the server gives no
  // per-row progress to poll (unlike the lead import's COMMITTING job), so
  // the bar below is indeterminate. It exists because a large selection
  // takes long enough that a static popup reads as frozen.
  const isSaving = assign.isPending;
  const leadCount = rows.length;

  return (
    <UzDialog
      open={open}
      // Locked while the assignment is in flight: closing mid-request would
      // leave the operator with no idea whether it landed, and the mutation
      // keeps running regardless.
      forceOpen={isSaving}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setAgentId('');
      }}
      title="Assign unassigned leads"
      description={`Reassign ${leadCount} selected lead${leadCount === 1 ? '' : 's'} from the exit queue to an active agent.`}
    >
      <div className="space-y-4 py-2">
        <UzSelect
          label="Assign to"
          placeholder="Select an agent"
          value={agentId}
          onValueChange={setAgentId}
          options={agents.options}
          disabled={agents.isLoading || isSaving}
        />
        {isSaving && (
          <div className="rounded-md border border-blue-200 bg-blue-50/60 px-4 py-3">
            <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 font-medium text-zinc-800">
                <UzSpinner size="xs" tone="primary" />
                Assigning leads…
              </span>
              <span className="text-zinc-600 tabular-nums">
                {leadCount.toLocaleString('en-IN')} lead{leadCount === 1 ? '' : 's'}
              </span>
            </div>
            {/* `skel` = the app's shimmer track (globals.css). Indeterminate
                by design - there is no server-side row counter to fill. */}
            <div className="skel h-2 w-full rounded-full" />
            <div className="mt-1.5 text-[11px] text-zinc-500">
              Please keep this window open until the assignment finishes.
            </div>
          </div>
        )}
        {!isSaving && assign.data && assign.data.failed.length > 0 && (
          <p className="text-sm text-red-600">
            {assign.data.updated} assigned, {assign.data.failed.length} failed.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </UzButton>
          <UzButton
            onClick={handleSave}
            loading={isSaving}
            disabled={!agentId || leadCount === 0 || isSaving}
          >
            {isSaving ? 'Assigning…' : 'Save assignment'}
          </UzButton>
        </div>
      </div>
    </UzDialog>
  );
};
